/**
 * deviceTableAdapter.js -- Transforms raw sysconfig data into the
 * table-friendly shape that DeviceSettings table components expect.
 */

import { fracToPct } from "../domain/units";
import { getCoupledDeviceIds } from "../domain/deviceSelectors";
import { TYPE_LABELS } from "../domain/deviceCoupling";

/**
 * The two coupling fields every couplable row carries: the partner ids (what
 * the edit form's picker starts from) and their names (what the table's
 * "Coupled:" line shows). Read from both sides of each link, so a sysconfig
 * that carries only one side still shows it.
 * @param {Object} sysconfig
 * @param {Object} dev - the raw sysconfig device
 * @param {Map<string, {name: string, type: string}>} byId - every couplable device
 */
function couplingFields(sysconfig, dev, byId) {
  const ids = dev.device_id ? getCoupledDeviceIds(sysconfig, dev.device_id) : [];
  return {
    coupled_device_ids: ids,
    coupled_device_names: ids.map((id) => {
      const partner = byId.get(id);
      return partner ? `${partner.name} (${TYPE_LABELS[partner.type] || partner.type})` : id;
    }),
  };
}

/**
 * id -> {name, type} over every couplable device on every meter.
 * @param {Object} sysconfig
 */
function indexCouplableDevices(sysconfig) {
  const byId = new Map();
  const meters = sysconfig?.Microgrid?.meters || [];
  meters.forEach((meter) => {
    [
      ["evs", "ev"],
      ["batteries", "battery"],
      ["solar_arrays", "solar"],
      ["fueled_generators", "generator"],
    ].forEach(([collection, type]) => {
      (meter[collection] || []).forEach((dev) => {
        if (dev.device_id) byId.set(dev.device_id, { name: dev.name || dev.device_id, type });
      });
    });
  });
  return byId;
}

/**
 * @param {Object} sysconfig
 * @returns {{ storage: Array, consumption: Array, production: Array, transfer: Array }}
 */
export function processDevicesForTable(sysconfig) {
  const storage = [];
  const consumption = [];
  const production = [];
  const transfer = [];
  const meters = sysconfig?.Microgrid?.meters || [];
  const couplableById = indexCouplableDevices(sysconfig);

  meters.forEach((meter) => {
    (meter.evs || []).forEach((ev) => {
      storage.push({
        device_id: ev.device_id || null,
        device_name: ev.name || "NA",
        make_and_model: ev.make_and_model || "NA",
        serial_number: ev.serial_number || "NA",
        device_type: "EV",
        storage_mode: "EV",
        status: ev.status ?? "connected",
        max_power_charging:
          ev.power_limits?.maximum_charging_power_kw ?? "NA",
        max_power_discharging:
          ev.power_limits?.maximum_discharging_power_kw ?? "NA",
        max_energy: ev.maximum_capacity_kwh ?? "NA",
        min_soc: ev.soc_limit_fractions?.min_soc_fraction ?? "NA",
        max_soc: ev.soc_limit_fractions?.max_soc_fraction ?? "NA",
        initial_soc: ev.initial_soc_fraction ?? "NA",
        avg_parasitic_load: ev.ave_parasitic_load_kw ?? "NA",
        fractional_self_loss: ev.fractional_self_discharge ?? "NA",
        charging_efficiency_fraction:
          ev.efficiency_fractions?.charging_efficiency_fraction ?? "NA",
        discharging_efficiency_fraction:
          ev.efficiency_fractions?.discharging_efficiency_fraction ?? "NA",
        daily_cycles: ev.daily_cycles ?? "NA",
        charging_strategy: ev.charging_strategy || "NA",
        discharging_strategy: ev.discharging_strategy || "NA",
        csv_file_name: ev.availability_schedule_csv || "NA",
        average_energy_used_during_operation:
          ev.ave_away_hourly_discharge_fraction != null
            ? fracToPct(ev.ave_away_hourly_discharge_fraction)
            : "NA",
        ...couplingFields(sysconfig, ev, couplableById),
      });
    });

    (meter.batteries || []).forEach((bess) => {
      storage.push({
        device_id: bess.device_id || null,
        device_name: bess.name || "NA",
        make_and_model: bess.make_and_model || "NA",
        serial_number: bess.serial_number || "NA",
        device_type: "BESS",
        storage_mode: "BESS",
        status: bess.status ?? "connected",
        max_power_charging:
          bess.power_limits?.maximum_charging_power_kw ?? "NA",
        max_power_discharging:
          bess.power_limits?.maximum_discharging_power_kw ?? "NA",
        max_energy: bess.maximum_capacity_kwh ?? "NA",
        min_soc: bess.soc_limit_fractions?.min_soc_fraction ?? "NA",
        max_soc: bess.soc_limit_fractions?.max_soc_fraction ?? "NA",
        initial_soc: bess.initial_soc_fraction ?? "NA",
        avg_parasitic_load: bess.ave_parasitic_load_kw ?? "NA",
        fractional_self_loss: bess.fractional_self_discharge ?? "NA",
        charging_efficiency_fraction:
          bess.efficiency_fractions?.charging_efficiency_fraction ?? "NA",
        discharging_efficiency_fraction:
          bess.efficiency_fractions?.discharging_efficiency_fraction ?? "NA",
        daily_cycles: bess.daily_cycles ?? "NA",
        charging_strategy: bess.charging_strategy || "NA",
        discharging_strategy: bess.discharging_strategy || "NA",
        ...couplingFields(sysconfig, bess, couplableById),
      });
    });

    (meter.uncontrollable_loads || []).forEach((load) => {
      consumption.push({
        device_id: load.device_id || null,
        device_name: load.name || "NA",
        make_and_model: load.make_and_model || "NA",
        serial_number: load.serial_number || "NA",
        device_type: "Consumption",
        // Some older configs may surface the status on building_load.
        status: load.status ?? meter.building_load?.status ?? "connected",
        peakPower: load.max_operating_power_kw ?? "NA",
        max_power_charging: load.max_operating_power_kw ?? "NA",
        csv_file_name:
          load.historical_load_csv ??
          meter.building_load?.historical_load_csv ??
          "NA",
      });
    });

    (meter.solar_arrays || []).forEach((solar) => {
      production.push({
        device_id: solar.device_id || null,
        device_name: solar.name || "NA",
        make_and_model: solar.make_and_model || "NA",
        serial_number: solar.serial_number || "NA",
        device_type: "Production",
        status: solar.status ?? "connected",
        production_type: "Solar PV",
        max_power_charging: solar.max_operating_power_kw ?? "NA",
        min_power_charging: solar.min_operating_power_kw ?? "NA",
        startup_cost: solar.startup_shutdown_cost ?? "NA",
        yearly_degradation: solar.degradation_yearly_kw_fraction ?? "NA",
        csv_file_name: solar.historical_production_csv || "NA",
        ...couplingFields(sysconfig, solar, couplableById),
      });
    });

    (meter.fueled_generators || []).forEach((gen) => {
      production.push({
        device_id: gen.device_id || null,
        device_name: gen.name || "NA",
        make_and_model: gen.make_and_model || "NA",
        serial_number: gen.serial_number || "NA",
        device_type: "Production",
        status: gen.status ?? "connected",
        production_type: "Fuel generator",
        max_power_charging: gen.max_operating_power_kw ?? "NA",
        min_power_charging: gen.min_operating_power_kw ?? "NA",
        startup_cost: gen.startup_shutdown_cost ?? "NA",
        fuel_type: gen.fuel_type || "NA",
        fuel_storage_size: gen.fuel_storage_size_kg ?? "NA",
        consumption: gen.consumption_kg_per_kwh ?? "NA",
        carbon_emissions: gen.carbon_emissions_g_per_kg ?? "NA",
        fuel_cost_per_kg: gen.fuel_cost_per_kg ?? "NA",
        fuel_cost_escalator_fraction:
          gen.fuel_cost_escalator_fraction ?? "NA",
        ...couplingFields(sysconfig, gen, couplableById),
      });
    });
  });

  meters.forEach((meter) => {
    if (meter.name || meter.utility_tariff_csv || meter.demand_charge) {
      transfer.push({
        device_id: meter.device_id || meter.serial_number || "NA",
        device_name: meter.name || "NA",
        make_and_model: meter.make_and_model || "NA",
        serial_number: meter.serial_number || "NA",
        device_type: "Transfer",
        status: "connected",
        demand_charge: meter.demand_charge ?? "NA",
        csv_file_name: meter.utility_tariff_csv || "NA",
      });
    }
  });

  return { storage, consumption, production, transfer };
}
